import { BaseNode } from '../hooks/useNearbyBaseNodes';
import {
  AZIMUTH_POLYGON,
  DEGREES_TO_RADIANS,
} from '../constants/geographicConstants';

export const getFrequenciesGHz = (node: BaseNode): string[] => {
  if (!node.carrier || !Array.isArray(node.carrier)) {
    return [];
  }
  return node.carrier
    .map((c) => Math.floor(c.frequency / 1000))
    .filter((f) => f > 0)
    .map((f) => f.toString());
};

export const hasActiveFrequency = (
  node: BaseNode,
  activeBands: string[]
): boolean => {
  if (!activeBands || activeBands.length === 0) {
    return false;
  }
  const frequenciesGHz = getFrequenciesGHz(node);
  return frequenciesGHz.some((f) => activeBands.includes(f));
};

export const createAzimuthPolygon = (node: BaseNode): number[][] | null => {
  if (
    node.azimuth === null ||
    node.azimuth === undefined ||
    !node.latitude ||
    !node.longitude
  ) {
    return null;
  }

  const lat = node.latitude;
  const lng = node.longitude;
  const azimuthAngle = node.azimuth * DEGREES_TO_RADIANS;
  const spreadAngle = AZIMUTH_POLYGON.SPREAD_ANGLE_DEGREES * DEGREES_TO_RADIANS;
  const distance = AZIMUTH_POLYGON.DISTANCE_DEGREES;
  const steps = AZIMUTH_POLYGON.STEPS;

  const arcPoints: number[][] = [];

  // Generate arc points
  for (let i = 0; i <= steps; i++) {
    const angle = azimuthAngle - spreadAngle + (i * 2 * spreadAngle) / steps;
    const latOffset = distance * Math.cos(angle);
    const lngOffset = distance * Math.sin(angle);

    // Adjust for Earth's curvature
    arcPoints.push([
      lng + lngOffset / Math.cos((lat * Math.PI) / 180),
      lat + latOffset,
    ]);
  }

  return [[lng, lat], ...arcPoints, [lng, lat]];
};

export const getSiteId = (node: BaseNode): string | null => {
  const site = node.site;
  if (!site) return null;

  return site;
};

export const groupBNsBySite = (
  nodes: BaseNode[]
): Record<string, BaseNode[]> => {
  return nodes.reduce(
    (acc, node) => {
      const siteId = getSiteId(node);
      if (siteId) {
        if (!acc[siteId]) {
          acc[siteId] = [];
        }
        acc[siteId].push(node);
      }
      return acc;
    },
    {} as Record<string, BaseNode[]>
  );
};

export const calculateAverageLocation = (
  nodes: BaseNode[]
): { latitude: number; longitude: number } | null => {
  if (nodes.length === 0) return null;

  const firstLat = nodes[0].latitude;
  const firstLng = nodes[0].longitude;
  const allSame = nodes.every(
    (node) => node.latitude === firstLat && node.longitude === firstLng
  );

  if (allSame) {
    return { latitude: firstLat, longitude: firstLng };
  }

  const sum = nodes.reduce(
    (acc, node) => ({
      lat: acc.lat + node.latitude,
      lng: acc.lng + node.longitude,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    latitude: sum.lat / nodes.length,
    longitude: sum.lng / nodes.length,
  };
};

export const createAzimuthArcFromSite = (
  siteLocation: { latitude: number; longitude: number },
  azimuth: number
): number[][] | null => {
  if (azimuth === null || azimuth === undefined) {
    return null;
  }

  const lat = siteLocation.latitude;
  const lng = siteLocation.longitude;
  const azimuthAngle = azimuth * DEGREES_TO_RADIANS;
  const spreadAngle = AZIMUTH_POLYGON.SPREAD_ANGLE_DEGREES * DEGREES_TO_RADIANS;
  const distance = AZIMUTH_POLYGON.DISTANCE_DEGREES;
  const steps = AZIMUTH_POLYGON.STEPS;

  const arcPoints: number[][] = [];

  for (let i = 0; i <= steps; i++) {
    const angle = azimuthAngle - spreadAngle + (i * 2 * spreadAngle) / steps;
    const latOffset = distance * Math.cos(angle);
    const lngOffset = distance * Math.sin(angle);

    // Adjust for Earth's curvature
    arcPoints.push([
      lng + lngOffset / Math.cos((lat * Math.PI) / 180),
      lat + latOffset,
    ]);
  }

  return [[lng, lat], ...arcPoints, [lng, lat]];
};
